import { useCallback, useEffect, useRef, useState } from "react";
import { ApiClientError } from "./api";

export type RemoteState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "empty"; data: T }
  | { status: "error"; message: string; httpStatus?: number };

export function useRemote<T>(loader: () => Promise<T>, isEmpty?: (value: T) => boolean) {
  const [state, setState] = useState<RemoteState<T>>({ status: "loading" });
  const isEmptyRef = useRef(isEmpty);
  const requestSequence = useRef(0);
  isEmptyRef.current = isEmpty;
  const reload = useCallback(async () => {
    const requestId = ++requestSequence.current;
    // 首次读取才进入整页 loading。筛选、队列切换和手动刷新已有内容时
    // 保留当前画面，避免 RemotePageState 被卸载后重新挂载造成整页闪烁。
    setState((current) => current.status === "ready" || current.status === "empty"
      ? current
      : { status: "loading" });
    try {
      const data = await loader();
      if (requestId !== requestSequence.current) return;
      setState(isEmptyRef.current?.(data) ? { status: "empty", data } : { status: "ready", data });
    } catch (error) {
      if (requestId !== requestSequence.current) return;
      // 保留 HTTP 状态码：403 等权限阻断由页面映射为 blocked 态，而不是普通错误。
      const httpStatus = error instanceof ApiClientError ? error.status : undefined;
      setState(httpStatus === undefined
        ? { status: "error", message: error instanceof Error ? error.message : "加载失败" }
        : { status: "error", message: error instanceof Error ? error.message : "加载失败", httpStatus });
    }
  }, [loader]);
  useEffect(() => {
    void reload();
    return () => { requestSequence.current += 1; };
  }, [reload]);
  return { state, reload };
}
